import React, { useEffect } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Upload,
  message,
  Avatar,
  Spin,
} from "antd";
import { EditOutlined, UserOutlined } from "@ant-design/icons";
import "./EditProfile.less";
import {
  useGetUserByIDQuery,
  useUpdateUserMutation,
} from "../../../redux/services/user";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const { Option } = Select;

const EditProfile = () => {
  const { student_id } = useSelector((state) => state.auth);
  const { user_id } = useSelector((state) => state.auth);
  const {
    data: user,
    error: userError,
    isLoading: userLoading,
    isFetching: userFetching,
  } = useGetUserByIDQuery(student_id);

  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [form] = Form.useForm();

  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        email: user?.user?.email || "",
        name: user?.user?.name || "",
        birthDate: user?.user?.birthDate ? dayjs(user?.user?.birthDate) : null,
        phoneNumber: `+${user?.country_code} ${user?.mobile_number}`,
      });
    }
  }, [user, form]);

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    return dayjs().diff(birthDate, "years");
  };

  const onFinish = async (values) => {
    try {
      const age = calculateAge(values.birthDate);
      const mobile_number = user?.mobile_number;

      const updateData = {
        name: values.name,
        email: values.email,
        mobile_number,
        age: age,
      };

      await updateUser({
        user_id: user_id,
        payload: updateData,
      });
      message.success("Profile updated successfully!");
    } catch (err) {
      message.error(err.data?.message || "Failed to update profile");
    }
  };

  const handleUpload = (info) => {
    if (info.file.status === "done") {
      message.success(`${info.file.name} file uploaded successfully.`);
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  return (
    <Spin spinning={userLoading || userFetching} tip='Loading profile...'>
      {userError
        ? message.error(
            userError.data?.message || "Failed to fetch user details"
          )
        : null}
      <div className='profile-form-container'>
        <div className='profile-image'>
          <Avatar
            src={user?.user?.profile_img}
            icon={user?.user?.profile_img ? null : <UserOutlined />}
            alt='Profile'
            className='profile-img'
          />

          <Upload
            name='avatar'
            showUploadList={false}
            action='/upload'
            onChange={handleUpload}
            className='upload-button'
          >
            <Button
              shape='circle'
              icon={<EditOutlined />}
              className='upload-icon'
            />
          </Upload>
        </div>

        <Form
          form={form}
          layout='vertical'
          onFinish={onFinish}
          className='profile-form'
        >
          <Form.Item
            name='name'
            label='Name'
            rules={[{ required: true, message: "Please input your name!" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name='email'
            label='Email Address'
            rules={[{ type: "email", message: "Invalid email format!" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name='birthDate'
            label='Birth Date'
            rules={[
              { required: true, message: "Please select your birth date!" },
            ]}
          >
            <DatePicker
              format='YYYY-MM-DD'
              style={{ width: "100%" }}
              disabledDate={(current) => current > dayjs().endOf("day")}
            />
          </Form.Item>

          <Form.Item name='phoneNumber' label='Phone Number'>
            <Input disabled />
          </Form.Item>

          <Form.Item>
            <Button
              type='primary'
              htmlType='submit'
              block
              loading={isUpdating}
              disabled={userLoading || userFetching}
            >
              {isUpdating ? "Updating..." : "Save"}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Spin>
  );
};

export default EditProfile;
